import './MonthlySalesView.css';

import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Suspense, useEffect, useMemo, useRef, useState } from 'react';

import { Btn } from '../../components/Btn';
import { LineTrend } from '../../components/charts';
import { ExportMenu } from '../../components/ExportMenu';
import { Select } from '../../components/Select';
import { DataTable } from '../../components/Table';
import type {
  MonthlyBranchSales,
  MonthlyProductSales,
} from '../../data/fetchers';
import { getMonthlySales, getMonthlySalesDetailed } from '../../data/fetchers';
import type { ExportCol } from '../../lib/export';
import {
  fmtAmount,
  fmtBaht,
  fmtBahtShort,
  fmtPct,
  fmtSatangCompact,
} from '../../lib/format';
import { useBranchScope } from '../../lib/useBranchScope';
import { MonthlyPromotions } from './MonthlyPromotions';
import { ReportHeader } from './ReportHeader';

const fullMonth = new Intl.DateTimeFormat('th-TH', {
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});
function monthLabel(key: string): string {
  return key ? fullMonth.format(new Date(`${key}-01T00:00:00Z`)) : '';
}

interface MonthExportRow {
  month: string;
  revenueBaht: string;
  revenueSatang: number;
  momPct: string;
  sharePct: string;
}
interface MonthlyProductExportRow {
  productId: string;
  name: string;
  qty: number;
  amountBaht: string;
  amountSatang: number;
}
const exportCols: ExportCol<MonthExportRow>[] = [
  { key: 'month', header: 'เดือน' },
  { key: 'revenueBaht', header: 'ยอดขาย' },
  { key: 'revenueSatang', header: 'ยอดขาย (satang)' },
  { key: 'momPct', header: 'Δ MoM' },
  { key: 'sharePct', header: 'สัดส่วนทั้งปี' },
];
const detailedProductExportCols: ExportCol<MonthlyProductExportRow>[] = [
  { key: 'productId', header: 'รหัสสินค้า' },
  { key: 'name', header: 'ชื่อสินค้า' },
  { key: 'qty', header: 'จำนวนที่ขาย' },
  { key: 'amountBaht', header: 'ยอดขายรวม' },
  { key: 'amountSatang', header: 'ยอดขายรวม (satang)' },
];
const Delta = ({ value }: { value: number | null | undefined }) =>
  value == null ? (
    <span className="text-ink-3">—</span>
  ) : (
    <span
      className={`num font-semibold ${value > 0 ? 'text-ok' : value < 0 ? 'text-danger' : 'text-ink-3'}`}
    >
      {value > 0 ? '▲ +' : value < 0 ? '▼ ' : '– '}
      {Math.abs(value * 100).toFixed(1)}%
    </span>
  );
const branchColumns: ColumnDef<
  MonthlyBranchSales & { share: number },
  unknown
>[] = [
  {
    accessorKey: 'branchName',
    header: 'สาขา',
    cell: ({ row }) => (
      <>
        <span>{row.original.branchName}</span>
        <span className="block text-sm-label text-ink-3">
          {fmtPct(row.original.share * 100)}
        </span>
      </>
    ),
  },
  {
    accessorKey: 'revenue',
    header: 'ยอดขาย (บาท)',
    meta: { align: 'right' },
    cell: (c) => (
      <span className="num whitespace-nowrap">
        {fmtAmount(c.getValue<number>())}
      </span>
    ),
  },
  {
    accessorKey: 'mom',
    header: 'MoM',
    meta: { align: 'right' },
    cell: (c) => <Delta value={c.getValue<number | null>()} />,
  },
];
const productColumns: ColumnDef<MonthlyProductSales, unknown>[] = [
  {
    id: 'rank',
    header: 'อันดับ',
    cell: ({ row }) => (
      <span
        className={`text-sm-label ${row.index < 3 ? 'text-primary' : 'text-ink-3'}`}
      >
        {row.index + 1}
      </span>
    ),
    enableSorting: false,
  },
  {
    accessorKey: 'name',
    header: 'สินค้า',
    meta: { className: 'monthly-product-name' },
  },
  {
    accessorKey: 'qty',
    header: 'จำนวน',
    meta: { align: 'right' },
    cell: (c) => (
      <span className="num">{c.getValue<number>().toLocaleString()}</span>
    ),
  },
  {
    accessorKey: 'amount',
    header: 'ยอดขาย (บาท)',
    meta: { align: 'right' },
    cell: (c) => (
      <span className="num whitespace-nowrap">
        {fmtAmount(c.getValue<number>())}
      </span>
    ),
  },
];

function ReportSkeleton({ detail = false }: { detail?: boolean }) {
  return (
    <div
      role="status"
      aria-label={
        detail ? 'กำลังโหลดรายละเอียดรายเดือน' : 'กำลังโหลดภาพรวมรายเดือน'
      }
      aria-busy="true"
      className="space-y-5"
    >
      <span className="sr-only">กำลังโหลดข้อมูล</span>
      <div aria-hidden="true" className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className="space-y-3 py-4">
            <div className="h-4 w-24 rounded bg-line-2" />
            <div className="h-8 w-32 max-w-full rounded bg-line-2" />
          </div>
        ))}
      </div>
      {detail && (
        <div aria-hidden="true" className="space-y-3 border-t border-line pt-5">
          {Array.from({ length: 10 }, (_, i) => (
            <div key={i} className="h-8 rounded bg-line-2" />
          ))}
        </div>
      )}
    </div>
  );
}

export function MonthlySalesView() {
  const { scope, ready } = useBranchScope();
  const [requestedMonth, setRequestedMonth] = useState('');
  const [annualSort, setAnnualSort] = useState<{
    key: 'key' | 'revenue' | 'mom' | 'share';
    descending: boolean;
  } | null>(null);
  const monthControls = useRef<HTMLDivElement>(null);
  const [desktop, setDesktop] = useState(
    () =>
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(min-width: 1024px)').matches,
  );
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia('(min-width: 1024px)');
    const update = () => setDesktop(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  const overview = useQuery({
    queryKey: ['report', 'monthly', scope.branchId, scope.allowedBranchIds],
    queryFn: () => getMonthlySales(scope),
    enabled: ready,
  });
  const data = overview.data;
  const selectedMonth = data?.monthKeys.includes(requestedMonth)
    ? requestedMonth
    : (data?.monthKeys.at(-1) ?? '');
  const selectedLabel = monthLabel(selectedMonth);
  const latestMonth = data?.monthKeys.at(-1) ?? '';
  const latestLabel = monthLabel(latestMonth);
  const year = latestMonth ? String(Number(latestMonth.slice(0, 4)) + 543) : '';
  const detail = useQuery({
    queryKey: [
      'report',
      'monthly-detail',
      selectedMonth,
      scope.branchId,
      scope.allowedBranchIds,
    ],
    queryFn: () => getMonthlySalesDetailed(selectedMonth, scope),
    enabled: ready && !overview.isError && !!selectedMonth,
  });
  const detailedData = detail.data;
  const series = data?.series ?? [];
  const labels = data?.labels ?? [];
  const ytd = data?.gross ?? 0;
  const bestIdx = series.length ? series.indexOf(Math.max(...series)) : -1;
  const currentMonth = series.at(-1) ?? 0;
  const scopeLabel = scope.branchId ? 'สาขาที่เลือก' : 'ทุกสาขาที่มีสิทธิ์';
  const rows = useMemo(() => {
    if (!data) return [];
    return data.monthKeys.map((key, i) => ({
      key,
      month: data.labels[i],
      revenue: data.series[i] ?? 0,
      mom: data.monthOverMonth[i] ?? null,
      share: data.gross ? (data.series[i] ?? 0) / data.gross : 0,
    }));
  }, [data]);
  const displayedRows = useMemo(() => {
    if (!annualSort) return rows;
    const { key, descending } = annualSort;
    return [...rows].sort((a, b) => {
      const difference =
        key === 'key'
          ? a.key.localeCompare(b.key)
          : (a[key] ?? -Infinity) - (b[key] ?? -Infinity);
      return (difference || a.key.localeCompare(b.key)) * (descending ? -1 : 1);
    });
  }, [rows, annualSort]);
  const exportRows = useMemo<MonthExportRow[]>(
    () =>
      rows.map((row) => ({
        month: row.month,
        revenueBaht: fmtBaht(row.revenue),
        revenueSatang: row.revenue,
        momPct: row.mom == null ? '—' : fmtPct(row.mom * 100),
        sharePct: fmtPct(row.share * 100),
      })),
    [rows],
  );
  const chartData = useMemo(
    () => rows.map((row) => ({ label: row.month, value: row.revenue })),
    [rows],
  );
  const branchRows = useMemo(
    () =>
      detailedData?.branches.map((row) => ({
        ...row,
        share: detailedData.gross ? row.revenue / detailedData.gross : 0,
      })) ?? [],
    [detailedData],
  );
  const categoryTotal = detailedData?.grossBeforeDiscount ?? 0;
  const selectedIndex = data?.monthKeys.indexOf(selectedMonth) ?? -1;
  const isEmpty =
    detailedData &&
    detailedData.gross === 0 &&
    detailedData.cogs === 0 &&
    detailedData.products.length === 0 &&
    detailedData.promotions.length === 0;

  function selectMonth(key: string) {
    setRequestedMonth(key);
    monthControls.current?.querySelector('select')?.focus();
  }

  const annualTable = (
    <table
      className="monthly-annual-table w-full text-body"
      aria-label="ยอดขายรายเดือน"
    >
      <thead>
        <tr>
          {(
            [
              ['key', 'เดือน'],
              ['revenue', 'ยอดขาย (บาท)'],
              ['mom', 'MoM'],
              ['share', 'สัดส่วน'],
            ] as const
          ).map(([key, label]) => (
            <th
              key={key}
              scope="col"
              className={`${key === 'share' ? 'monthly-share ' : ''}${key === 'key' ? '' : 'text-right'}`}
              aria-sort={
                annualSort?.key === key
                  ? annualSort.descending
                    ? 'descending'
                    : 'ascending'
                  : 'none'
              }
            >
              <button
                type="button"
                className="monthly-sort-button"
                aria-label={`เรียงตาม${label}`}
                onClick={() =>
                  setAnnualSort((current) =>
                    current?.key !== key
                      ? { key, descending: false }
                      : !current.descending
                        ? { key, descending: true }
                        : null,
                  )
                }
              >
                {label}
                {annualSort?.key === key
                  ? annualSort.descending
                    ? ' ↓'
                    : ' ↑'
                  : ''}
              </button>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {displayedRows.map((row) => (
          <tr key={row.key} aria-selected={row.key === selectedMonth}>
            <td>
              <button
                type="button"
                onClick={() => selectMonth(row.key)}
                aria-label={`ดูรายละเอียด${monthLabel(row.key)}`}
                aria-pressed={row.key === selectedMonth}
                className="monthly-month-button"
              >
                {row.month}
              </button>
            </td>
            <td className="num text-right">{fmtAmount(row.revenue)}</td>
            <td className="text-right">
              <Delta value={row.mom} />
            </td>
            <td className="monthly-share num text-right">
              {fmtPct(row.share * 100)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
  const annualOverview = data ? (
    <section
      aria-label="แนวโน้มและยอดรายเดือน"
      className="bo-card grid min-w-0 gap-6 p-4 sm:p-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]"
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-h3 font-semibold">แนวโน้มปี {year}</h2>
          <span className="text-sm-label text-ink-3">หน่วย: บาท</span>
        </div>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm-label text-ink-3">
          {bestIdx >= 0 && (
            <span>
              สูงสุด {labels[bestIdx]}{' '}
              <b className="font-medium text-ink">
                {fmtBahtShort(series[bestIdx])}
              </b>
            </span>
          )}
          <span>
            เฉลี่ย{' '}
            <b className="font-medium text-ink">
              {fmtBahtShort(series.length ? ytd / series.length : 0)}
            </b>
            /เดือน
          </span>
        </div>
        <div
          className="mt-3"
          role="img"
          aria-label={`กราฟยอดขายปี ${year} เดือนที่เลือก ${selectedLabel}`}
        >
          <Suspense fallback={<div className="h-[280px] rounded bg-line-2" />}>
            <LineTrend
              data={chartData}
              height={280}
              selectedLabel={
                rows.find((row) => row.key === selectedMonth)?.month
              }
              valueFormat={fmtBahtShort}
              tickFormat={fmtSatangCompact}
            />
          </Suspense>
        </div>
      </div>
      {desktop ? (
        <section aria-label="ตารางยอดขายรายเดือน" className="min-w-0">
          <h2 className="mb-2 text-h3 font-semibold">ยอดขายรายเดือน</h2>
          {annualTable}
        </section>
      ) : (
        <details className="monthly-annual-disclosure border-t border-line pt-3">
          <summary className="min-h-11 cursor-pointer py-2 font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-ink">
            ยอดขายรายเดือน
            <span className="mt-1 block text-sm-label font-normal text-ink-3">
              มกราคม–{latestLabel}
            </span>
          </summary>
          {annualTable}
        </details>
      )}
    </section>
  ) : null;

  return (
    <div className="monthly-sales mx-auto max-w-[1400px] space-y-5 font-sans [&_h1]:font-sans">
      <ReportHeader
        title="ยอดขายรายเดือน"
        subtitle={
          year
            ? `ปี ${year} · ${scopeLabel}`
            : 'แนวโน้มยอดขายและรายละเอียดรายเดือน'
        }
        status={{
          label:
            !ready || overview.isLoading
              ? 'กำลังประมวลผล'
              : overview.isError
                ? 'โหลดข้อมูลไม่สำเร็จ'
                : `${rows.length} เดือนในรายงาน`,
          tone:
            !ready || overview.isLoading
              ? 'neutral'
              : overview.isError
                ? 'danger'
                : 'ok',
        }}
        actions={
          !overview.isError && data ? (
            <ExportMenu
              rows={exportRows}
              cols={exportCols}
              filename="ยอดขายรายเดือน"
              title={`ยอดขายรายเดือน ปี ${year}`}
              label="ส่งออกภาพรวม"
              pdf
            />
          ) : undefined
        }
      />
      {overview.isError ? (
        <div role="alert" className="bo-card space-y-3 p-6">
          <p>โหลดรายงานยอดขายรายเดือนไม่สำเร็จ</p>
          <Btn onClick={() => void overview.refetch()}>ลองโหลดอีกครั้ง</Btn>
        </div>
      ) : (
        <>
          <section aria-labelledby="monthly-overview-heading">
            <h2
              id="monthly-overview-heading"
              className="mb-2 text-sm-label font-medium text-ink-3"
            >
              ภาพรวม {latestLabel ? `มกราคม–${latestLabel}` : 'ปีปัจจุบัน'}
            </h2>
            {!ready || overview.isLoading ? (
              <ReportSkeleton />
            ) : (
              <dl className="bo-card grid grid-cols-2 py-0 sm:grid-cols-[1.15fr_1fr_1fr] sm:py-4">
                <div className="monthly-summary-metric col-span-2 border-b border-line p-4 sm:col-span-1 sm:border-b-0">
                  <dt className="text-body text-ink-3">
                    เดือนปัจจุบัน{' '}
                    <span className="block text-sm-label">{latestLabel}</span>
                  </dt>
                  <dd className="num my-1 text-[28px] font-semibold leading-snug tracking-tight">
                    {fmtBaht(currentMonth)}
                  </dd>
                  <dd className="flex flex-wrap gap-x-2 text-sm-label">
                    <Delta value={data?.monthOverMonth.at(-1)} />
                    <span className="text-ink-3">เทียบเดือนก่อน (MoM)</span>
                  </dd>
                </div>
                <div className="monthly-summary-metric border-r border-line p-4 sm:border-l">
                  <dt className="text-body text-ink-3">สะสมทั้งปี (YTD)</dt>
                  <dd className="num my-1 text-2xl font-semibold leading-snug tracking-tight">
                    {fmtBaht(ytd)}
                  </dd>
                  <dd className="text-sm-label text-ink-3">
                    เฉลี่ย{' '}
                    {fmtBahtShort(series.length ? ytd / series.length : 0)}
                    /เดือน
                  </dd>
                </div>
                <div className="monthly-summary-metric p-4">
                  <dt className="text-body text-ink-3">คาดการณ์เดือนหน้า</dt>
                  <dd className="num my-1 text-2xl font-semibold leading-snug tracking-tight">
                    {fmtBaht(data?.forecast ?? 0)}
                  </dd>
                  <dd className="text-sm-label text-ink-3">
                    ค่าเฉลี่ย 3 เดือนล่าสุด
                  </dd>
                </div>
              </dl>
            )}
          </section>
          {desktop && annualOverview}
          <section
            aria-labelledby="monthly-detail-heading"
            className="bo-card min-w-0 space-y-5 p-4 sm:p-5"
          >
            <div className="flex flex-wrap items-end justify-between gap-4 border-b border-line pb-4">
              <div>
                <h2
                  id="monthly-detail-heading"
                  className="text-h3 font-semibold"
                >
                  {selectedLabel
                    ? `รายละเอียด ${selectedLabel}`
                    : 'รายละเอียดรายเดือน'}
                </h2>
                <p className="mt-1 text-sm-label text-ink-3">
                  ยอดขายและกำไรของเดือนที่เลือก · {scopeLabel}
                </p>
              </div>
              <div
                ref={monthControls}
                className="monthly-detail-controls grid min-w-0 gap-3 sm:flex sm:items-end"
              >
                <label className="block min-w-0">
                  <span className="mb-1.5 block text-sm-label text-ink-3">
                    เดือนที่ต้องการดู
                  </span>
                  <Select
                    aria-label="เดือนที่ต้องการดู"
                    value={selectedMonth}
                    disabled={!data?.monthKeys.length}
                    onChange={(event) => setRequestedMonth(event.target.value)}
                    wrapperClassName="w-full sm:w-52"
                  >
                    {!data?.monthKeys.length && (
                      <option value="">กำลังโหลดเดือน</option>
                    )}
                    {data?.monthKeys.map((key) => (
                      <option key={key} value={key}>
                        {monthLabel(key)}
                      </option>
                    ))}
                  </Select>
                </label>
                {detailedData &&
                  !detail.isError &&
                  !detail.isLoading &&
                  !isEmpty && (
                    <ExportMenu
                      rows={detailedData.products.map((product) => ({
                        productId: product.productId,
                        name: product.name,
                        qty: product.qty,
                        amountBaht: fmtBaht(product.amount),
                        amountSatang: product.amount,
                      }))}
                      cols={detailedProductExportCols}
                      filename={`ยอดขายรายสินค้า - เดือน ${selectedMonth}`}
                      title={`รายงานยอดขายสินค้าเจาะลึก - เดือน ${selectedMonth}`}
                      label="ส่งออกรายสินค้า"
                      pdf
                    />
                  )}
              </div>
            </div>
            {detail.isError ? (
              <div role="alert" className="space-y-3 py-8">
                <h3 className="font-semibold">
                  โหลดรายละเอียดรายเดือนไม่สำเร็จ
                </h3>
                <p className="text-body text-ink-3">
                  ตรวจสอบการเชื่อมต่ออินเทอร์เน็ต แล้วลองโหลดข้อมูลอีกครั้ง
                </p>
                <Btn onClick={() => void detail.refetch()}>
                  ลองโหลดรายละเอียดอีกครั้ง
                </Btn>
              </div>
            ) : !ready ||
              overview.isLoading ||
              detail.isLoading ||
              !detailedData ? (
              <ReportSkeleton detail />
            ) : isEmpty ? (
              <div className="space-y-3 py-10">
                <h3 className="text-h3 font-semibold">
                  ยังไม่มียอดขายในเดือน{selectedLabel}
                </h3>
                <p className="max-w-xl text-body text-ink-3">
                  ยอดขายจะแสดงเมื่อมีรายการที่ซิงก์เข้าระบบแล้ว
                  เลือกเดือนอื่นเพื่อดูรายงานย้อนหลังได้
                </p>
                {selectedIndex > 0 && (
                  <Btn
                    onClick={() =>
                      selectMonth(data!.monthKeys[selectedIndex - 1])
                    }
                  >
                    ดูเดือนก่อนหน้า
                  </Btn>
                )}
              </div>
            ) : (
              <div
                key={selectedMonth}
                className={`${requestedMonth ? 'monthly-detail-content ' : ''}space-y-5`}
              >
                <dl className="grid grid-cols-2 gap-4 sm:grid-cols-[1.2fr_1fr_1fr]">
                  <div className="col-span-2 sm:col-span-1">
                    <dt className="text-body text-ink-3">ยอดขายสุทธิ</dt>
                    <dd className="num mt-1 text-[28px] font-semibold">
                      {fmtBaht(detailedData.gross)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-body text-ink-3">กำไรขั้นต้น</dt>
                    <dd
                      className={`num mt-1 text-2xl font-semibold ${detailedData.grossProfit < 0 ? 'text-danger' : ''}`}
                    >
                      {fmtBaht(detailedData.grossProfit)}
                    </dd>
                    <dd className="text-sm-label text-ink-3">
                      ยอดขายสุทธิ − COGS
                    </dd>
                  </div>
                  <div>
                    <dt className="text-body text-ink-3">อัตรากำไรขั้นต้น</dt>
                    <dd
                      className={`num mt-1 text-2xl font-semibold ${detailedData.grossProfit < 0 ? 'text-danger' : ''}`}
                    >
                      {detailedData.gross > 0
                        ? `${((detailedData.grossProfit / detailedData.gross) * 100).toFixed(1)}%`
                        : '—'}
                    </dd>
                  </div>
                </dl>
                {detailedData.legacyAccountingReceiptCount > 0 && (
                  <p className="rounded-btn bg-warn-light px-3 py-2 text-body text-warn">
                    มีข้อมูลเก่า{' '}
                    {detailedData.legacyAccountingReceiptCount.toLocaleString()}{' '}
                    บิลที่ยังไม่มีการแยกยอดก่อนส่วนลด
                    ระบบใช้ยอดสุทธิของบิลเหล่านั้น และไม่นำโปรโมชั่นเดิมมารวมซ้ำ
                  </p>
                )}
                <dl
                  aria-label="ที่มาของยอดสุทธิ"
                  className="flex flex-wrap gap-x-5 gap-y-3 border-b border-line pb-5 text-sm-label"
                >
                  {[
                    ['ยอดก่อนส่วนลด', detailedData.grossBeforeDiscount],
                    [
                      'มูลค่าโปรโมชั่น',
                      detailedData.promotionDiscount
                        ? -detailedData.promotionDiscount
                        : 0,
                    ],
                    ...(detailedData.manualLineDiscount
                      ? [['ส่วนลดรายสินค้า', -detailedData.manualLineDiscount]]
                      : []),
                    [
                      'ส่วนลดท้ายบิล',
                      detailedData.billDiscount
                        ? -detailedData.billDiscount
                        : 0,
                    ],
                    ['ยอดปัดเศษ', detailedData.roundingDelta],
                    ['ต้นทุนขาย (COGS)', detailedData.cogs],
                  ].map(([label, value]) => (
                    <div key={String(label)}>
                      <dt className="text-ink-3">{label}</dt>
                      <dd className="num mt-1 font-medium">
                        {fmtBaht(Number(value))}
                      </dd>
                    </div>
                  ))}
                </dl>
                <div className="grid min-w-0 items-start gap-6 xl:grid-cols-[minmax(0,1.8fr)_minmax(0,1fr)]">
                  <section
                    aria-labelledby="monthly-products-heading"
                    className="monthly-table monthly-products-table min-w-0"
                  >
                    <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                      <h3
                        id="monthly-products-heading"
                        className="text-h3 font-semibold"
                      >
                        สินค้าขายดี 10 อันดับแรก
                      </h3>
                      <span className="text-sm-label text-ink-3">
                        อันดับตามจำนวนขาย
                      </span>
                    </div>
                    <DataTable
                      columns={productColumns}
                      data={detailedData.topProducts}
                      pageSize={10}
                      emptyMessage="ไม่มีข้อมูลยอดขายรายสินค้า"
                    />
                  </section>
                  <div className="grid min-w-0 gap-6 md:grid-cols-2 xl:grid-cols-1">
                    <section
                      aria-labelledby="monthly-branches-heading"
                      className="monthly-table min-w-0"
                    >
                      <h3
                        id="monthly-branches-heading"
                        className="mb-2 text-h3 font-semibold"
                      >
                        ยอดขายแยกตามสาขา
                      </h3>
                      <DataTable
                        columns={branchColumns}
                        data={branchRows}
                        pageSize={Number.MAX_SAFE_INTEGER}
                        emptyMessage="ไม่มีข้อมูลสาขาสำหรับเดือนนี้"
                      />
                    </section>
                    <section aria-labelledby="monthly-categories-heading">
                      <h3
                        id="monthly-categories-heading"
                        className="mb-2 text-h3 font-semibold"
                      >
                        ยอดขายแยกตามหมวดหมู่
                      </h3>
                      {detailedData.categories.length === 0 ? (
                        <p className="py-4 text-body text-ink-3">
                          ไม่มีข้อมูลหมวดหมู่สำหรับเดือนนี้
                        </p>
                      ) : (
                        <ul className="space-y-3">
                          {detailedData.categories.map((category) => {
                            const share =
                              categoryTotal > 0
                                ? category.amount / categoryTotal
                                : 0;
                            return (
                              <li key={category.categoryId}>
                                <div className="flex items-baseline justify-between gap-3 text-body">
                                  <span className="min-w-0 break-words">
                                    {category.categoryName || 'ไม่ระบุหมวดหมู่'}
                                  </span>
                                  <span className="num whitespace-nowrap text-right">
                                    {fmtAmount(category.amount)}{' '}
                                    <span className="text-sm-label text-ink-3">
                                      ({fmtPct(share * 100)})
                                    </span>
                                  </span>
                                </div>
                                <div
                                  className="mt-1 h-1 overflow-hidden rounded bg-line-2"
                                  aria-hidden="true"
                                >
                                  <div
                                    className="h-full rounded bg-primary"
                                    style={{
                                      width: `${Math.min(100, Math.max(0, share * 100))}%`,
                                    }}
                                  />
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </section>
                  </div>
                </div>
                <MonthlyPromotions
                  promotions={detailedData.promotions}
                  monthLabel={selectedLabel}
                  scopeLabel={scopeLabel}
                />
              </div>
            )}
          </section>
          {!desktop && annualOverview}
          <p className="text-sm-label text-ink-3">
            ยอดเงินแสดงเป็นบาท · {scopeLabel}
          </p>
        </>
      )}
    </div>
  );
}
